// Thin adapter that mimics the old @codetrix-studio/capacitor-google-auth
// surface (`GoogleAuth.initialize / signOut / signIn`) but is backed by the
// Capacitor 8-compatible @capgo/capacitor-social-login plugin.
//
// Why an adapter and not a straight rewrite of the consumer:
//   - The rest of the app (GoogleSignInButton, tests, and downstream error
//     handling for cancelled sign-ins) already speaks the old plugin's
//     shape. Keeping that shape lets us cap the migration blast radius to
//     this one file and swap the plugin under everyone else.
//   - The backend endpoint /api/auth/google/oauth-token requires a Google
//     OAuth *access_token* (posted as `access_token`, snake_case). The
//     old plugin exposed that as `result.authentication.accessToken`. This
//     adapter builds the same shape from Capgo's response so downstream
//     code (`result?.authentication?.accessToken`) keeps working untouched.
//
// Web/PWA/iOS callers never reach this file — GoogleSignInButton only
// imports it when running inside the native Capacitor Android WebView.
//
// CLIENT ID SOURCE OF TRUTH
// -------------------------
// The web-side value comes from REACT_APP_GOOGLE_CLIENT_ID in
// /app/frontend/.env. Rotating this key requires updating THREE places
// (Android build outputs are baked at APK build time — a plain env swap
// only rotates the web/PWA build):
//   1. /app/frontend/.env                                            (this file's source)
//   2. /app/frontend/android/app/src/main/res/values/strings.xml      (native fallback)
//   3. /app/frontend/android/app/src/main/assets/capacitor.config.json (Capgo runtime cfg)
// DO NOT run `npx cap sync android` to regenerate (2)+(3) — the app's
// custom FCM Java/Manifest setup would be clobbered. Hand-edit instead.
import { SocialLogin } from '@capgo/capacitor-social-login';

const WEB_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

let initialized = false;

async function initialize(_opts) {
  if (initialized) return;
  if (!WEB_CLIENT_ID) {
    // Fail fast with a legible error if someone strips REACT_APP_GOOGLE_CLIENT_ID
    // from the deployed build — much easier to debug than the opaque
    // "DEVELOPER_ERROR (10)" that Google Play Services would raise later.
    throw new Error(
      'REACT_APP_GOOGLE_CLIENT_ID is not set — cannot initialise Google Sign-In.'
    );
  }
  await SocialLogin.initialize({
    google: {
      webClientId: WEB_CLIENT_ID,
      // "online" so we get an OAuth access_token (compatible with the
      // existing backend `/api/auth/google/oauth-token` endpoint, which
      // calls Google's userinfo endpoint using that access token). Do NOT
      // switch to "offline" — that returns a serverAuthCode which the
      // backend does not know how to exchange.
      mode: 'online',
    },
  });
  initialized = true;
}

async function signOut() {
  // Clears the Google credential state so the account picker is shown on
  // the next sign-in. This is best-effort; if there's no active session
  // Capgo throws — we swallow it to match the old plugin's behaviour.
  await SocialLogin.logout({ provider: 'google' });
}

async function signIn() {
  const response = await SocialLogin.login({
    provider: 'google',
    options: {
      scopes: ['openid', 'profile', 'email'],
      // These two together defeat Android auto-select and force the
      // native account chooser every time — matching how the old plugin
      // behaved after signOut().
      filterByAuthorizedAccounts: false,
      autoSelectEnabled: false,
    },
  });

  if (response?.provider !== 'google') {
    throw new Error('Unexpected social-login provider');
  }

  const result = response.result || {};
  // In online mode Capgo returns { accessToken: { token, ... }, idToken, profile }.
  const accessToken =
    (result.accessToken && result.accessToken.token) || null;
  const idToken = result.idToken || null;
  const profile = result.profile || {};

  if (!accessToken) {
    throw new Error('Google did not return an OAuth access token.');
  }

  // Shape the return value like the old plugin so GoogleSignInButton can
  // keep reading `result.authentication.accessToken` unchanged.
  return {
    authentication: {
      accessToken,
      idToken,
    },
    email: profile.email || '',
    name: profile.name || '',
    imageUrl: profile.imageUrl || profile.picture || '',
  };
}

export const GoogleAuth = {
  initialize,
  signOut,
  signIn,
};
